/**
 * QRScanner — kameraskannigsmodal for QR-kode-innsjekking.
 *
 * Bruker `html5-qrcode` for kameraadgang og QR-dekoding (iPhone/Safari og Android/Chrome over HTTPS).
 * Faller tilbake til manuell liming hvis kamera avslås eller ikke støttes.
 *
 * QR-format brukt av TravelEase:
 *   tlease://checkin?type=p&id={passenger_id}&trip={trip_number}     (booking-passasjerer)
 *   tlease://checkin?type=w&id={walkin_id}&trip={trip_number}        (walk-ins)
 *
 * Manuell liming godtar også den 6-tegns referansen fra e-posten («Hvis QR ikke vises …») når `onRef` er satt.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { ScanLine, X, AlertCircle, Camera, Keyboard, Loader2, Volume2, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { isShortRef } from '@/lib/checkinRef';
import { scanFeedback, primeScanFeedback, isScanSoundOn, setScanSoundOn, type ScanOutcome } from '@/lib/scanFeedback';

export interface QRScanResult {
  type: 'p' | 'w';
  id: string;
  trip: string;
}

export type { ScanOutcome };

export interface QRScannerProps {
  expectedTripNumber?: string;
  /** Returner gjerne utfallet ('ok' | 'already' | 'not_found' | 'error') — styrer lyd/vibrasjon. Uten returverdi = 'ok'. */
  onScan: (result: QRScanResult) => Promise<void | ScanOutcome> | void | ScanOutcome;
  onRef?: (ref: string) => Promise<void | ScanOutcome> | void | ScanOutcome;
  onClose: () => void;
}

const SCANNER_ELEMENT_ID = 'travelease-qr-scanner-region';
/** Samme QR-tekst ignoreres så lenge (hindrer dobbel-skann og gjentatte «allerede sjekket inn»-toasts når koden holdes foran kameraet). */
const SAME_PAYLOAD_MS = 10000;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export function parseQRPayload(text: string): QRScanResult | null {
  try {
    if (!text.startsWith('tlease://checkin')) return null;
    const url = new URL(text.replace('tlease://', 'https://localhost/'));
    const type = url.searchParams.get('type');
    const id = url.searchParams.get('id');
    const trip = url.searchParams.get('trip');
    if ((type !== 'p' && type !== 'w') || !id || !trip) return null;
    return { type, id, trip };
  } catch {
    return null;
  }
}

const cameraSupported = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && (window.isSecureContext !== false);

/** Permissions-Policy (tidl. Feature-Policy) kan blokkere kamera for hele nettstedet — gi en presis melding i stedet for «avslått». */
const cameraAllowedByPolicy = () => {
  const fp = (document as any).permissionsPolicy || (document as any).featurePolicy;
  if (fp && typeof fp.allowsFeature === 'function') {
    try { return !!fp.allowsFeature('camera'); } catch { return true; }
  }
  return true;
};

/** Stopp + rydd en html5-qrcode-instans trygt (stop() kaster synkront når den ikke kjører). Idempotent. */
const disposeScanner = async (s: Html5Qrcode | null, regionId: string) => {
  if (!s) return;
  // html5-qrcode kaller video.play() uten catch — fjernes videoen før den faktisk spiller, får vi en uhåndtert AbortError.
  // NB: `paused` blir false idet play() kalles; bruk readyState for å se om avspillingen faktisk er i gang. Vent på «playing» (maks 1,5 s).
  const video = document.querySelector(`#${regionId} video`) as HTMLVideoElement | null;
  if (video && video.srcObject && (video.paused || video.readyState < 3)) {
    await Promise.race([new Promise<void>((r) => video.addEventListener('playing', () => r(), { once: true })), sleep(1500)]);
  }
  try {
    // getState() blir SCANNING idet start() løser — før videoen har begynt å spille (isScanning settes først ved «playing»).
    const st = s.getState();
    if (st === Html5QrcodeScannerState.SCANNING || st === Html5QrcodeScannerState.PAUSED) await s.stop();
  } catch { /* ignorer */ }
  try { s.clear(); } catch { /* ignorer */ }
  // Sikkerhetsnett: lukk ev. gjenværende kameraspor (intern strøm + videoelementer i regionen).
  try {
    const stream = (s as any).renderedCamera?.mediaStream as MediaStream | undefined;
    stream?.getTracks().forEach((tr) => tr.stop());
  } catch { /* ignorer */ }
  document.querySelectorAll(`#${regionId} video`).forEach((v) => {
    const stream = (v as HTMLVideoElement).srcObject as MediaStream | null;
    stream?.getTracks().forEach((tr) => tr.stop());
  });
};

let regionSeq = 0;

export const QRScanner: React.FC<QRScannerProps> = ({ expectedTripNumber, onScan, onRef, onClose }) => {
  const { t } = useTranslation();
  // Egen region-id per montering — en døende instans og en ny deler aldri element.
  const regionIdRef = useRef(`${SCANNER_ELEMENT_ID}-${++regionSeq}`);
  const regionId = regionIdRef.current;
  // Kjede som serialiserer start/stopp av html5-qrcode-instanser (StrictMode / rask lukking må aldri etterlate en åpen kamerastrøm).
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const lastScannedRef = useRef<{ id: string; at: number } | null>(null);
  const busyRef = useRef(false);
  const closingRef = useRef(false);
  // Foreldrenes callbacks holdes i refs slik at kamera-effekten aldri restartes av en re-render.
  const onScanRef = useRef(onScan); onScanRef.current = onScan;
  const onRefRef = useRef(onRef); onRefRef.current = onRef;
  const onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  const expectedRef = useRef(expectedTripNumber); expectedRef.current = expectedTripNumber;
  const tRef = useRef(t); tRef.current = t;

  const [mode, setMode] = useState<'camera' | 'manual' | 'closing'>('camera');
  const [cameraState, setCameraState] = useState<'starting' | 'scanning' | 'error'>('starting');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [soundOn, setSoundOn] = useState<boolean>(() => isScanSoundOn());

  // Lås opp lyd i brukerhandlingen som åpnet skanneren (iOS krever gest).
  useEffect(() => { primeScanFeedback(); }, []);

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setScanSoundOn(next);
  };

  /** Én innsjekking om gangen — dobbel-skann/dobbeltklikk blir ignorert mens forrige pågår. Gir lyd/vibrasjon etter utfall. */
  const submit = useCallback(async (fn: () => Promise<void | ScanOutcome> | void | ScanOutcome): Promise<boolean> => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setSubmitting(true);
    try {
      const outcome = (await fn()) || 'ok';
      scanFeedback(outcome);
      return outcome === 'ok' || outcome === 'already';
    } catch {
      scanFeedback('error');
      toast.error(tRef.current('qRScanner.innsjekkingFeilet', 'Innsjekking feilet'));
      return false;
    } finally {
      busyRef.current = false;
      setSubmitting(false);
    }
  }, []);

  const validate = useCallback((text: string, invalidMsg: string): QRScanResult | null => {
    const parsed = parseQRPayload(text);
    if (!parsed) {
      scanFeedback('error');
      toast.error(invalidMsg);
      return null;
    }
    const expected = expectedRef.current;
    if (expected && parsed.trip !== expected) {
      scanFeedback('error');
      toast.error(tRef.current('qRScanner.qrTilhorerTurIkke', { defaultValue: 'QR tilhører tur {{v1}}, ikke {{v2}}', v1: parsed.trip, v2: expected }));
      return null;
    }
    return parsed;
  }, []);

  // Stabil dekode-callback (ingen deps) — html5-qrcode fanger denne én gang ved start().
  const handleDecoded = useCallback((decoded: string) => {
    if (busyRef.current || closingRef.current) return;
    const now = Date.now();
    if (lastScannedRef.current && lastScannedRef.current.id === decoded && (now - lastScannedRef.current.at) < SAME_PAYLOAD_MS) return;
    lastScannedRef.current = { id: decoded, at: now };
    const parsed = validate(decoded, tRef.current('qRScanner.ukjentQrFormat', 'Ukjent QR-format'));
    if (!parsed) return;
    void submit(() => onScanRef.current(parsed));
  }, [submit, validate]);

  useEffect(() => {
    if (mode !== 'camera') return undefined;
    const tt = tRef.current;
    const finishCloseIfRequested = () => { if (closingRef.current) onCloseRef.current(); };

    if (!cameraSupported()) {
      setErrorMsg(tt('qRScanner.kameraIkkeStottet', 'Kamera-skanning krever HTTPS og en nettleser med kamerastøtte (Safari på iPhone, Chrome på Android). Lim inn koden manuelt i stedet.'));
      setCameraState('error');
      return finishCloseIfRequested;
    }
    if (!cameraAllowedByPolicy()) {
      setErrorMsg(tt('qRScanner.kameraBlokkertAvPolicy', 'Kamera er blokkert av nettstedets sikkerhetsinnstillinger (Permissions-Policy). Lim inn koden manuelt i stedet.'));
      setCameraState('error');
      return finishCloseIfRequested;
    }

    let cancelled = false;
    let instance: Html5Qrcode | null = null;
    const run = chainRef.current.then(async () => {
      if (cancelled || !document.getElementById(regionId)) return;
      const html5 = new Html5Qrcode(regionId, /* verbose */ false);
      instance = html5;
      try {
        await html5.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1 },
          (decoded) => handleDecoded(decoded),
          /* onScanError = stille — fyrer hver frame uten match */ () => {}
        );
        // Lukket/restartet mens start() pågikk → stopp kameraet med en gang (cleanup-kjeden rydder også, idempotent).
        if (cancelled) { await disposeScanner(html5, regionId); return; }
        setCameraState('scanning');
      } catch (err) {
        if (cancelled) { await disposeScanner(html5, regionId); return; }
        const msg = err instanceof Error ? err.message : String(err);
        const name = (err as any)?.name || '';
        if (/permission|NotAllowed|denied/i.test(`${name} ${msg}`)) {
          setErrorMsg(tt('qRScanner.kameraAvslatt', 'Kamera-tilgang ble avslått. Tillat kamera for dette nettstedet i nettleserinnstillingene, eller lim inn koden manuelt.'));
        } else if (/NotFound|no camera|OverconstrainedError|NotReadable/i.test(`${name} ${msg}`)) {
          setErrorMsg(tt('qRScanner.ingenKamera', 'Fant ikke et brukbart kamera på enheten. Lim inn koden manuelt i stedet.'));
        } else {
          setErrorMsg(tt('qRScanner.kunneIkkeStarteKamera', { defaultValue: 'Kunne ikke starte kamera: {{msg}}', msg }));
        }
        setCameraState('error');
      }
    });
    chainRef.current = run.catch(() => {});

    return () => {
      cancelled = true;
      // Vent til start() har landet før kameraet stoppes — ellers blir MediaStream-sporet stående «live» (kamera-LED på).
      // Ved lukking holdes modalen (og videoelementet) i DOM til kameraet er stoppet, deretter kalles onClose.
      chainRef.current = Promise.race([chainRef.current, sleep(4000)])
        .then(() => disposeScanner(instance, regionId))
        .catch(() => {})
        .then(finishCloseIfRequested);
    };
  }, [mode, attempt, handleDecoded, regionId]);

  /** Lukk pent: stopp kameraet først (ingen «play() interrupted»-feil), så gi beskjed til forelderen. */
  const requestClose = () => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (mode === 'camera') setMode('closing');
    else onCloseRef.current();
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode]);

  const retryCamera = () => {
    setErrorMsg(null);
    setCameraState('starting');
    setAttempt((a) => a + 1);
    setMode('camera');
  };

  const switchToManual = () => setMode('manual');

  const handleManualSubmit = async () => {
    if (busyRef.current) return;
    const text = manualInput.trim();
    if (!text) return;
    const tt = tRef.current;
    if (onRefRef.current && isShortRef(text) && !text.startsWith('tlease://')) {
      const ok = await submit(() => onRefRef.current!(text.toUpperCase()));
      if (ok) setManualInput('');
      return;
    }
    const parsed = validate(text, tt('qRScanner.ugyldigQrTekst', 'Ugyldig QR-tekst'));
    if (!parsed) return;
    const ok = await submit(() => onScanRef.current(parsed));
    if (ok) setManualInput('');
  };

  const showManualPanel = mode === 'manual' || cameraState === 'error';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end md:items-center justify-center bg-black/70"
      onClick={requestClose}
      role="dialog"
      aria-modal="true"
      data-state="open"
      data-testid="qr-scanner-modal"
    >
      <div
        className="bg-background w-full md:max-w-md md:rounded-lg rounded-t-2xl shadow-2xl flex flex-col pb-[env(safe-area-inset-bottom)]"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={primeScanFeedback}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-primary" />
            <span className="font-bold text-base">{t('qRScanner.skannQrKode', 'Skann QR-kode')}</span>
            {expectedTripNumber && <span className="text-xs font-mono text-muted-foreground" data-testid="qr-scanner-trip">{expectedTripNumber}</span>}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={toggleSound}
              className={`p-2 rounded-full hover:bg-accent ${soundOn ? 'text-foreground' : 'text-muted-foreground'}`}
              aria-label={soundOn ? t('qRScanner.lydAv', 'Skru av skannelyd') : t('qRScanner.lydPa', 'Skru på skannelyd')}
              aria-pressed={soundOn}
              title={soundOn ? t('qRScanner.lydAv', 'Skru av skannelyd') : t('qRScanner.lydPa', 'Skru på skannelyd')}
              data-testid="qr-sound-toggle"
            >
              {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            <button onClick={requestClose} className="p-2 hover:bg-accent rounded-full" aria-label={t('qRScanner.lukk', 'Lukk')} data-testid="qr-scanner-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          {/* Kamera-regionen holdes alltid i DOM (skjult i manuell modus) så en døende html5-qrcode-instans aldri mister videoelementet før den er stoppet. */}
          <div className={showManualPanel ? 'hidden' : 'relative w-full aspect-square bg-black rounded-lg overflow-hidden'}>
            <div id={regionId} className="w-full h-full" />
            {cameraState === 'starting' && mode !== 'closing' && (
              <div className="absolute inset-0 flex items-center justify-center text-white" data-testid="qr-scanner-starting">
                <Camera className="w-8 h-8 animate-pulse" />
                <span className="ml-2">{t('qRScanner.starterKamera', 'Starter kamera...')}</span>
              </div>
            )}
            {cameraState === 'scanning' && mode === 'camera' && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center" data-testid="qr-scanner-active">
                <div className="w-60 h-60 border-2 border-white/80 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.4)]" />
              </div>
            )}
            {mode === 'closing' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm" data-testid="qr-scanner-closing">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('qRScanner.lukker', 'Lukker…')}
              </div>
            )}
            {submitting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-white text-sm" data-testid="qr-scanner-submitting">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {t('qRScanner.sjekkerInn', 'Sjekker inn…')}
              </div>
            )}
          </div>
          {showManualPanel ? (
            <>
              {cameraState === 'error' && errorMsg && (
                <div className="flex items-start gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm" data-testid="qr-scanner-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{errorMsg}</span>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-2" htmlFor="qr-manual-input">
                  {onRef ? t('qRScanner.limInnQrEllerRef', 'Lim inn QR-tekst eller skriv 6-tegns referansen fra e-posten') : t('qRScanner.limInnQrTekstManuelt', 'Lim inn QR-tekst manuelt')}
                </label>
                <input
                  id="qr-manual-input"
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && manualInput.trim() && !submitting) { void handleManualSubmit(); } }}
                  placeholder={onRef ? t('qRScanner.placeholderRef', 'tlease://checkin?… eller f.eks. 3F9A2B') : t('qRScanner.tleaseCheckinTypePId', 'tlease://checkin?type=p&id=...')}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={submitting}
                  className="w-full h-10 px-3 border border-border rounded-md bg-background text-sm font-mono"
                  data-testid="qr-manual-input"
                />
                <Button
                  className="w-full mt-2"
                  onClick={handleManualSubmit}
                  disabled={!manualInput.trim() || submitting}
                  data-testid="qr-manual-submit"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {t('qRScanner.sjekkInn', 'Sjekk inn')}
                </Button>
              </div>
              <button
                type="button"
                onClick={retryCamera}
                className="text-xs text-primary underline"
                data-testid="qr-camera-retry"
              >
                {t('qRScanner.provKameraIgjen', 'Prøv kamera igjen')}
              </button>
            </>
          ) : (
            <>
              <p className="text-xs text-center text-muted-foreground">
                {t('qRScanner.holdQrKodenInnenforRammen', 'Hold QR-koden innenfor rammen')}
              </p>
              <button
                type="button"
                onClick={switchToManual}
                disabled={mode === 'closing'}
                className="w-full inline-flex items-center justify-center gap-2 text-xs text-primary py-2 hover:bg-accent rounded disabled:opacity-50"
                data-testid="qr-manual-toggle"
              >
                <Keyboard className="w-3 h-3" />
                {t('qRScanner.limInnManueltIStedet', 'Lim inn manuelt i stedet')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QRScanner;
